import { useQuery } from '@tanstack/react-query';
import apiClient from '@/services/apiClient';
import { billingApi, type Invoice } from '@/services/api/billingApi';
import { appointmentApi, type Appointment } from '@/services/appointmentApi';
import { auditLogsApi, type AuditLogEntry } from '@/services/api/auditLogsApi';
import { useAuthStore } from '@/stores/useAuthStore';
import type { Range } from './scope';

/**
 * WHAT THE MANAGER'S SCREENS READ. One file, because every one of his reports
 * is the same three collections sliced a different way — invoices, appointments
 * and the log — and a hook per screen means a hook per screen drifts.
 *
 * TWO RULES THE BACKEND IMPOSES, both of which cost a real bug elsewhere:
 *
 *  - LIST ENDPOINTS DEFAULT TO limit=20 (BRIEF §4.5). A month of invoices is
 *    ~90 rows; asking without a limit silently reports a fifth of the month as
 *    the month. Every query here asks for PAGE_MAX and carries the server's
 *    `total` back so the screen can state its own truncation.
 *  - THE ENVELOPE. `{ success, data, pagination }` — unwrapped at the caller
 *    (BRIEF §4.4), never inside a component.
 */

/**
 * THE SERVER'S CEILING. `PaginationDto` validates `limit <= 100` and REFUSES
 * anything larger with a 400 — asking for 500 does not truncate, it fails the
 * whole screen. So a period wider than 100 rows is paged, not asked for in one
 * breath.
 */
const PAGE_MAX = 100;

/** How many pages a single screen will walk before it says "showing N of M". */
const MAX_PAGES = 12;

/**
 * Walk a paginated list to the end (or to MAX_PAGES) and hand back the rows
 * WITH THE SERVER'S TOTAL — which is what a truncation notice must print
 * (BRIEF §4.12), never `rows.length`.
 */
async function walk<T>(
  fetchPage: (page: number, limit: number) => Promise<{ rows: T[]; total: number }>,
): Promise<Page<T>> {
  const first = await fetchPage(1, PAGE_MAX);
  const rows = [...first.rows];
  const pages = Math.min(MAX_PAGES, Math.ceil(first.total / PAGE_MAX) || 1);
  for (let p = 2; p <= pages; p += 1) {
    const next = await fetchPage(p, PAGE_MAX);
    if (!next.rows.length) break;
    rows.push(...next.rows);
  }
  return { rows, total: first.total };
}

export interface Page<T> {
  rows: T[];
  /** The server's count, not `rows.length` — what a "showing N of M" must use. */
  total: number;
}

const EMPTY: Page<never> = { rows: [], total: 0 };

/* ---------------- money ---------------- */

/**
 * Every invoice dated inside the range.
 *
 * `date` is the calendar day the bill belongs to, which is what a day's ledger
 * and a month's revenue are both counted on. `createdAt` would count a bill
 * back-dated this morning into today instead of into the day it is for.
 */
export function useInvoices(range: Range | null, enabled = true) {
  const q = useQuery({
    queryKey: ['cm-invoices', range?.start, range?.end],
    enabled: enabled && !!range,
    staleTime: 60_000,
    retry: false,
    queryFn: () =>
      walk<Invoice>(async (page, limit) => {
        const res = await billingApi.getInvoices({
          startDate: range!.start, endDate: range!.end, page, limit,
        });
        return { rows: res.data ?? [], total: res.pagination?.total ?? (res.data ?? []).length };
      }),
  });
  return { ...q, page: (q.data ?? EMPTY) as Page<Invoice> };
}

/* ---------------- visits ---------------- */

export function useAppointments(range: Range | null, enabled = true) {
  const q = useQuery({
    queryKey: ['cm-appointments', range?.start, range?.end],
    enabled: enabled && !!range,
    staleTime: 60_000,
    retry: false,
    queryFn: () =>
      walk<Appointment>(async (page, limit) => {
        const res = await appointmentApi.getAppointments({
          startDate: range!.start, endDate: range!.end, page, limit,
        });
        return { rows: res.data ?? [], total: res.pagination?.total ?? (res.data ?? []).length };
      }),
  });
  return { ...q, page: (q.data ?? EMPTY) as Page<Appointment> };
}

/* ---------------- who works here ---------------- */

export interface ProviderRow {
  id: string;
  firstName: string;
  lastName: string;
  role: string;
}

/**
 * The clinic's providers. `/users/providers` is the list every role may read —
 * `/users` itself is gated on `user:admin` on some deployments, and a manager
 * report has no business reading staff records to print a name.
 */
export function useProviders() {
  const q = useQuery({
    queryKey: ['cm-providers'],
    staleTime: 300_000,
    retry: false,
    queryFn: async (): Promise<ProviderRow[]> => {
      const res = await apiClient.get('/users/providers');
      return (res.data?.data ?? res.data ?? []) as ProviderRow[];
    },
  });
  return { ...q, providers: q.data ?? [] };
}

/* ---------------- the log ---------------- */

export interface AuditQuery {
  category?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
}

export function useAuditLog(params: AuditQuery, enabled = true) {
  const q = useQuery({
    queryKey: ['cm-audit', params.category, params.search, params.startDate, params.endDate, params.limit],
    enabled,
    staleTime: 30_000,
    retry: false,
    queryFn: async (): Promise<Page<AuditLogEntry>> => {
      /* The limit goes AFTER the spread: `{ limit: 50, ...params }` with an
         absent `params.limit` puts `undefined` back over the default, and the
         list silently falls to the server's 20 (BRIEF §4.5). */
      const res = await auditLogsApi.getAuditLogs({ ...params, limit: params.limit ?? 50 });
      /* The audit controller wraps its own paginated body inside the global
         envelope, so this one is `data.data` — two layers, not one. */
      const body = (res as unknown as { data?: unknown }).data ?? res;
      const inner = (body as { data?: AuditLogEntry[]; total?: number });
      return { rows: inner.data ?? [], total: inner.total ?? (inner.data ?? []).length };
    },
  });
  return { ...q, page: (q.data ?? EMPTY) as Page<AuditLogEntry> };
}

/* ---------------- unsigned clinical work ---------------- */

export interface DraftRecord {
  id: string;
  providerId?: string;
  visitDate?: string;
  status?: string;
}

/** Notes still unsigned — the manager sees the COUNT, never the note. */
export function useUnsignedNotes(enabled = true) {
  const q = useQuery({
    queryKey: ['cm-unsigned'],
    enabled,
    staleTime: 60_000,
    retry: false,
    queryFn: () =>
      walk<DraftRecord>(async (page, limit) => {
        const res = await apiClient.get('/medical-records', {
          params: { status: 'draft', page, limit },
        });
        const body = res.data ?? {};
        const rows = (body.data ?? []) as DraftRecord[];
        return { rows, total: body.pagination?.total ?? rows.length };
      }),
  });
  return { ...q, page: (q.data ?? EMPTY) as Page<DraftRecord> };
}

/* ---------------- what is waiting ---------------- */

export interface AlertRow {
  id: string;
  title: string;
  message: string;
  category?: string;
  type?: string;
  priority?: string;
  createdAt: string;
  readAt?: string | null;
  acknowledgedAt?: string | null;
  requiresAcknowledgment?: boolean;
  actionUrl?: string | null;
  user?: { id: string; firstName?: string; lastName?: string; role?: { name?: string } } | null;
}

/** Localised title/message columns arrive as `{ en, ar }`. */
function text(v: unknown): string {
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object') {
    const o = v as Record<string, string>;
    return o.en ?? o.ar ?? '';
  }
  return '';
}

export function useAlerts() {
  const q = useQuery({
    queryKey: ['cm-alerts'],
    staleTime: 30_000,
    retry: false,
    queryFn: async (): Promise<Page<AlertRow>> => {
      const res = await apiClient.get('/notifications', { params: { limit: PAGE_MAX } });
      const body = res.data ?? {};
      const raw = (body.data ?? []) as Array<Record<string, unknown>>;
      const rows: AlertRow[] = raw.map((n) => ({
        id: String(n.id),
        title: text(n.title),
        message: text(n.message),
        category: n.category as string | undefined,
        type: n.type as string | undefined,
        priority: n.priority as string | undefined,
        createdAt: String(n.createdAt ?? n.sentAt ?? ''),
        readAt: (n.readAt as string | null) ?? null,
        acknowledgedAt: (n.acknowledgedAt as string | null) ?? null,
        requiresAcknowledgment: Boolean(n.requiresAcknowledgment),
        actionUrl: (n.actionUrl as string | null) ?? null,
        user: (n.user as AlertRow['user']) ?? null,
      }));
      return { rows, total: body.pagination?.total ?? rows.length };
    },
  });
  return { ...q, page: (q.data ?? EMPTY) as Page<AlertRow> };
}

/* ---------------- the message wallet ---------------- */

export interface WalletSummary {
  balanceMinor: number;
  currency: string;
  autoReload: {
    enabled: boolean;
    thresholdMinor: number | null;
    reloadAmountMinor: number | null;
    /** Auto-reload is Stripe-only (off-session charging). */
    supported?: boolean;
    /** Without a saved card it fires, fails and emails the owner. */
    cardOnFile?: boolean;
    state?: string;
  };
  /** The last 25 movements — the only burn rate the backend records. */
  ledger?: Array<{ id: string; createdAt: string; deltaMinor: number; entryType?: string }>;
}

/**
 * THE BOARD'S WALLET ROW IS A RUNWAY — "Message wallet — 5 weeks left" — not a
 * balance, because time-to-empty is the thing a manager acts on. The only burn
 * rate the backend records is the 25-row ledger `/me/wallet` returns with the
 * balance, so the runway is computed from the debits in it and is `null` when
 * there are none. A balance printed where a runway belongs is a different
 * statement; so is a zero.
 */
export function walletRunwayWeeks(w: WalletSummary | null): number | null {
  if (!w || w.balanceMinor <= 0) return null;
  const debits = (w.ledger ?? []).filter((l) => l.deltaMinor < 0);
  if (!debits.length) return null;
  const oldest = debits.reduce((a, b) => (a.createdAt < b.createdAt ? a : b));
  const days = Math.max(1, (Date.now() - new Date(oldest.createdAt).getTime()) / 86_400_000);
  const spentMinor = debits.reduce((sum, l) => sum + Math.abs(l.deltaMinor), 0);
  const perWeek = (spentMinor / days) * 7;
  if (perWeek <= 0) return null;
  return Math.floor(w.balanceMinor / perWeek);
}

export function useWallet() {
  const q = useQuery({
    queryKey: ['cm-wallet'],
    staleTime: 300_000,
    retry: false,
    queryFn: async (): Promise<WalletSummary | null> => {
      const res = await apiClient.get('/me/wallet');
      return (res.data?.data ?? res.data ?? null) as WalletSummary | null;
    },
  });
  return { ...q, wallet: q.data ?? null };
}

/* ---------------- the clinic's shape ---------------- */

export interface ClinicShape {
  shape: string;
  members: number;
  providers: number;
  branches: number;
}

export function useClinicShape() {
  const q = useQuery({
    queryKey: ['cm-shape'],
    staleTime: 600_000,
    retry: false,
    queryFn: async (): Promise<ClinicShape | null> => {
      const res = await apiClient.get('/me/clinic-shape');
      return (res.data?.data ?? res.data ?? null) as ClinicShape | null;
    },
  });
  return { ...q, shape: q.data ?? null };
}

export interface ManagerBranch {
  /** Null only for the clinic-name fallback, before `/branches` answers. */
  id: string | null;
  name: string;
  isDefault: boolean;
}

/**
 * THE CLINIC'S SITES, the default first — `GET /branches`. Until it answers
 * (or for a clinic with no row yet) the clinic itself stands in, named from
 * the session's tenant so a screen never prints the canvas's "Maadi" at a
 * clinic that is not Maadi.
 */
export function useBranches(): ManagerBranch[] {
  return useBranchesState().branches;
}

/** The branches, and whether the answer is still on its way — until it is, a
 *  clinic with two sites reads as one and every per-branch figure is wrong. */
export function useBranchesState(): { branches: ManagerBranch[]; isLoading: boolean } {
  const tenant = useAuthStore((s) => s.activeTenant);
  const q = useQuery({
    queryKey: ['branches'],
    staleTime: 300_000,
    retry: false,
    queryFn: async () => {
      const res = await apiClient.get('/branches');
      const rows = (res.data?.data ?? res.data) as Array<{ id: string; name: string; isDefault: boolean }>;
      return Array.isArray(rows) ? rows : [];
    },
  });
  const rows = q.data ?? [];
  if (!rows.length) {
    return {
      branches: [{ id: null, name: tenant?.displayName?.trim() || 'This clinic', isDefault: true }],
      isLoading: q.isLoading,
    };
  }
  return {
    branches: [...rows]
      .sort((a, b) => Number(b.isDefault) - Number(a.isDefault))
      .map((r) => ({ id: r.id, name: r.name, isDefault: r.isDefault })),
    isLoading: q.isLoading,
  };
}

/** What a bill asks for. A cancelled bill asked for nothing — counting it makes
 *  "billed" 250 more than collected + owed can ever reach. */
export const billedOf = (i: Pick<Invoice, 'status' | 'total'>): number =>
  i.status === 'cancelled' ? 0 : Number(i.total || 0);

export interface BranchMoney {
  id: string | null;
  name: string;
  collected: number;
  billed: number;
  bills: number;
  unpaid: number;
}

/**
 * A SET OF BILLS, SPLIT BY THE SITE THAT RAISED THEM. A bill with no branch
 * predates the column and the backfill placed every one it could at the
 * default branch, so a null lands there too. Every branch gets a row, so a
 * site that took nothing this month reads 0 rather than vanishing.
 */
export function splitByBranch(invoices: Invoice[], branches: ManagerBranch[]): BranchMoney[] {
  const fallback = branches.find((b) => b.isDefault) ?? branches[0];
  const rows = new Map<string, BranchMoney>(
    branches.map((b) => [b.id ?? 'clinic', { id: b.id, name: b.name, collected: 0, billed: 0, bills: 0, unpaid: 0 }]),
  );
  for (const i of invoices) {
    const key = (i.branchId && rows.has(i.branchId) ? i.branchId : fallback?.id) ?? 'clinic';
    const row = rows.get(key);
    if (!row) continue;
    row.collected += Number(i.amountPaid || 0);
    row.billed += billedOf(i);
    row.bills += 1;
    if (Number(i.balance || 0) > 0 && i.status !== 'cancelled') row.unpaid += 1;
  }
  return [...rows.values()];
}

/* ---------------- results waiting to be read ---------------- */

export interface ResultsBacklog {
  /** Resulted labs + completed imaging nobody has marked reviewed. */
  unreviewed: number;
  /** False when neither stats endpoint answered — print "not recorded", not 0. */
  recorded: boolean;
}

/**
 * THE SECOND CLAUSE OF "NEEDS ATTENTION", which the boards carry on Today and
 * in the digest: "· 1 result unreviewed 3 d".
 *
 * Both halves are real endpoints — `GET /clinic-lab-orders/stats` and
 * `GET /imaging-orders/stats` each return an `unreviewed` count (a resulted /
 * completed order with `reviewed = false`) and the Clinic Manager holds
 * `lab_order:read` and `imaging_order:read`. What neither carries is HOW LONG
 * one has been waiting — there is no age in the stats and no `reviewed` filter
 * on the list endpoints — so the count is printed and the "3 d" is not
 * invented. Logged as a BACKEND-GAP.
 *
 * Neither is clinic-day-scoped: this is the standing backlog, which is what a
 * "needs attention" line is about.
 */
export function useUnreviewedResults() {
  const q = useQuery({
    queryKey: ['cm-unreviewed'],
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<ResultsBacklog> => {
      const read = async (path: string): Promise<number | null> => {
        try {
          const res = await apiClient.get(path);
          const body = (res.data?.data ?? res.data ?? {}) as { unreviewed?: number };
          return typeof body.unreviewed === 'number' ? body.unreviewed : null;
        } catch {
          return null;
        }
      };
      const [labs, imaging] = await Promise.all([
        read('/clinic-lab-orders/stats'),
        read('/imaging-orders/stats'),
      ]);
      if (labs === null && imaging === null) return { unreviewed: 0, recorded: false };
      return { unreviewed: (labs ?? 0) + (imaging ?? 0), recorded: true };
    },
  });
  return { ...q, results: q.data ?? { unreviewed: 0, recorded: false } };
}

/* ---------------- who arrived, and from where ---------------- */

/**
 * New patients registered inside a range.
 *
 * `/patients` takes NO date filter — its FilterPatientDto has search, name,
 * gender, blood type and active, and the global pipe runs
 * `forbidNonWhitelisted`, so inventing `startDate` there is a 400, not a
 * narrower query. The list is paged in full (a clinic's roster, not its
 * visits) and `createdAt` is counted here.
 */
export function usePatientRoster() {
  const q = useQuery({
    queryKey: ['cm-patient-roster'],
    staleTime: 300_000,
    retry: false,
    queryFn: async () => {
      let page = 1;
      let total = 0;
      const days: string[] = [];
      for (;;) {
        const res = await apiClient.get('/patients', { params: { page, limit: 100 } });
        const body = res.data ?? {};
        const rows = (body.data ?? []) as Array<{ createdAt?: string }>;
        total = Number(body.pagination?.total ?? rows.length);
        for (const r of rows) if (r.createdAt) days.push(String(r.createdAt).slice(0, 10));
        if (!rows.length || page * 100 >= total || page >= 10) break;
        page += 1;
      }
      return { days, roster: total };
    },
  });
  return { days: q.data?.days ?? EMPTY_DAYS, roster: q.data?.roster ?? 0 };
}

const EMPTY_DAYS: string[] = [];

export interface SourceChannel {
  channel: string;
  count: number;
  details: Array<{ detail: string; count: number }>;
}

/**
 * HOW NEW PATIENTS FOUND THE CLINIC — `GET /patients/sources`, counted on the
 * server from the "how did they hear about us" line both Add-patient forms
 * ask. "Not said" is a channel too, so the rows add up to the month.
 */
export function usePatientSources(range: { start: string; end: string }) {
  const q = useQuery({
    queryKey: ['cm-patient-sources', range.start, range.end],
    staleTime: 300_000,
    retry: false,
    queryFn: async () => {
      const res = await apiClient.get('/patients/sources', { params: { from: range.start, to: range.end } });
      const body = (res.data?.data ?? res.data) as { total: number; channels: SourceChannel[] } | null;
      return body ?? { total: 0, channels: [] };
    },
  });
  return { total: q.data?.total ?? 0, channels: q.data?.channels ?? EMPTY_CHANNELS, isLoading: q.isLoading };
}

const EMPTY_CHANNELS: SourceChannel[] = [];

export function useNewPatients(range: { start: string; end: string }) {
  const { days, roster } = usePatientRoster();
  const total = days.filter((d) => d >= range.start && d <= range.end).length;
  return { total, roster };
}

/**
 * Inbound referrals recorded inside a range, and who sent them.
 *
 * `/referrals` accepts only `patientId` and `status`, so the whole list is read
 * and narrowed here — passing `limit` or a date is refused outright by the
 * whitelist.
 */
export function useInboundReferrals(range: { start: string; end: string }) {
  const q = useQuery({
    queryKey: ['cm-referrals', range.start, range.end],
    staleTime: 300_000,
    retry: false,
    queryFn: async () => {
      const res = await apiClient.get('/referrals');
      const body = res.data ?? {};
      const rows = (body.data ?? []) as Array<Record<string, unknown>>;
      const inWindow = rows.filter((r) => {
        if (r.direction !== 'inbound') return false;
        const d = String(r.receivedOn ?? r.createdAt ?? '').slice(0, 10);
        return d >= range.start && d <= range.end;
      });
      const counts = new Map<string, number>();
      for (const r of inWindow) {
        const name = String(r.referredFromName ?? '').trim() || 'Not named';
        counts.set(name, (counts.get(name) ?? 0) + 1);
      }
      return {
        count: inWindow.length,
        byProvider: [...counts.entries()].map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count),
      };
    },
  });
  return { count: q.data?.count ?? 0, byProvider: q.data?.byProvider ?? [] };
}


/* ---------------- who has a login here ---------------- */

export interface MemberRow {
  userId: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  /** The PRIMARY role — their home screen and their sidebar. */
  roleName?: string;
  /**
   * Every OTHER switch that is on. One account, several roles: these add their
   * permissions to the same login rather than being a second account.
   */
  additionalRoles?: string[];
  status: string;
  joinedAt?: string;
  lastActiveAt?: string | null;
  linkedDoctorId?: string | null;
  seatKind?: string;
  /** "When away, results go to" — a doctor's cover. */
  coveringDoctorId?: string | null;
  /** Printed identity. */
  title?: string | null;
  degrees?: string | null;
  syndicateNo?: string | null;
  /** Whether the person has a signature image on file (never the image). */
  hasSignature?: boolean;
}

/**
 * THE CLINIC'S MEMBERSHIPS — one row per human with a login here.
 *
 * `GET /tenants/:id/members` is gated on `requireManagerOrOwner`, which is
 * exactly this role, and it is the ONLY list that carries a person's ROLE.
 * `/users/providers` (which the reports use) carries clinicians and no role
 * assignment, so a screen about roles cannot be built on it.
 *
 * The response is a plain array — this controller does not paginate — so there
 * is no `limit` to omit and nothing is silently truncated (BRIEF §4.5).
 */
export function useMembers() {
  const tenantId = useAuthStore((s) => s.activeTenant?.id ?? null);
  const q = useQuery({
    queryKey: ['cm-members', tenantId],
    enabled: !!tenantId,
    staleTime: 120_000,
    retry: false,
    queryFn: async (): Promise<MemberRow[]> => {
      const res = await apiClient.get(`/tenants/${tenantId}/members`);
      return (res.data?.data ?? res.data ?? []) as MemberRow[];
    },
  });
  return { ...q, members: q.data ?? [] };
}

/* ---------------- insurance claims, as the desk files them ---------------- */

export interface DeskClaim {
  id: string;
  insurer: string;
  amount: number;
  /** What the insurer paid; null until a remittance lands. */
  paidAmount: number | null;
  status: 'draft' | 'held' | 'filed' | 'approved' | 'rejected' | 'paid';
  filedAt: string | null;
  reason: string | null;
}

/**
 * The desk's claim records — `GET /reception-desk/billing/claims`. Unlike the
 * invoice-derived claims, these carry what was claimed, what the insurer paid
 * and why a rejected one came back, which is what a deduction rate and a
 * rejection list are made of.
 */
export function useDeskClaims() {
  const q = useQuery({
    queryKey: ['cm-desk-claims'],
    staleTime: 120_000,
    retry: false,
    queryFn: async () => {
      const res = await apiClient.get('/reception-desk/billing/claims', { params: { status: 'all' } });
      /* The desk answers { today, rows } (a bare array in older builds). */
      const body = (res.data?.data ?? res.data) as DeskClaim[] | { rows?: DeskClaim[] } | null;
      const rows = Array.isArray(body) ? body : body?.rows;
      return Array.isArray(rows) ? rows : [];
    },
  });
  return { rows: q.data ?? EMPTY_CLAIMS, isLoading: q.isLoading };
}

const EMPTY_CLAIMS: DeskClaim[] = [];
